/* eslint-disable */
import { API_URL } from '../../config';
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { symbolFor } from '../../utils/currencyTotals';


export default function QuoteSubmission() {
  const { token } = useParams();
  const [state, setState]           = useState('loading');
  const [job, setJob]               = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg]     = useState('');
  const amountRef = useRef(null);
  const notesRef  = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/quote/${token}`)
      .then(r => {
        if (r.status === 410) { setState('expired'); return null; }
        if (r.status === 409) { setState('used');    return null; }
        if (!r.ok)            { setState('error');   return null; }
        return r.json();
      })
      .then(data => {
        if (!data) return;
        setJob(data);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amountVal = amountRef.current?.value || '';
    const notesVal  = notesRef.current?.value  || '';
    if (!amountVal || parseFloat(amountVal) <= 0) { setErrorMsg('Please enter a valid quote amount'); return; }
    setSubmitting(true);
    setErrorMsg('');
    try {
      const r = await fetch(`${API_URL}/api/quote/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: parseFloat(amountVal), notes: notesVal }),
      });
      if (!r.ok) {
        const data = await r.json();
        setErrorMsg(data.error || 'Failed to submit quote');
        setSubmitting(false);
        return;
      }
      setState('submitted');
    } catch (e) {
      setErrorMsg('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', emergency: '🚨 Emergency' };
  const PRIORITY_COLOURS = { low: '#166534', medium: '#1e40af', high: '#9a3412', emergency: '#991b1b' };

  const Card = ({ children }) => (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: '0.75rem', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', maxWidth: '520px', width: '100%', overflow: 'hidden' }}>
        <div style={{ background: '#1a3c34', padding: '1.5rem 2rem' }}>
          <img src="/casavello-icon-500.png" alt="casavello" style={{ width: '40px', height: '40px', objectFit: 'contain', borderRadius: '8px', marginBottom: '0.75rem', display: 'block' }} />
          <h1 style={{ color: '#fff', margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>casavello Property Manager</h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>Maintenance Quote Request</p>
        </div>
        <div style={{ padding: '2rem' }}>{children}</div>
      </div>
    </div>
  );

  if (state === 'loading') return (
    <Card>
      <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⏳</div>
        <p>Loading job details...</p>
      </div>
    </Card>
  );

  if (state === 'expired') return (
    <Card>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏰</div>
        <h2 style={{ color: '#dc2626', marginBottom: '0.5rem' }}>Link Expired</h2>
        <p style={{ color: '#6b7280', lineHeight: 1.6 }}>This quote request link has expired. Please contact the property manager to request a new link.</p>
      </div>
    </Card>
  );

  if (state === 'used') return (
    <Card>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
        <h2 style={{ color: '#166534', marginBottom: '0.5rem' }}>Quote Already Submitted</h2>
        <p style={{ color: '#6b7280', lineHeight: 1.6 }}>A quote has already been submitted for this job. The property manager will be in touch.</p>
      </div>
    </Card>
  );

  if (state === 'error') return (
    <Card>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <h2 style={{ color: '#dc2626', marginBottom: '0.5rem' }}>Invalid Link</h2>
        <p style={{ color: '#6b7280', lineHeight: 1.6 }}>This quote link is invalid. Please contact the property manager directly.</p>
      </div>
    </Card>
  );

  if (state === 'submitted') return (
    <Card>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
        <h2 style={{ color: '#166534', marginBottom: '0.5rem' }}>Quote Submitted</h2>
        <p style={{ color: '#6b7280', lineHeight: 1.6, marginBottom: '1.5rem' }}>
          Your quote has been submitted successfully. The property manager will review and be in touch shortly.
        </p>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '1rem', textAlign: 'left' }}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>Job Reference</div>
          <div style={{ fontWeight: 600 }}>{job?.title}</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>{job?.property_address}</div>
        </div>
      </div>
    </Card>
  );

  // Ready state — show form
  return (
    <Card>
      <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem', fontWeight: 700 }}>Quote Request</h2>
      <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
        Please review the job details below and submit your quote.
      </p>

      {/* Job details */}
      <div style={{ background: '#f8fafc', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem', border: '1px solid #e5e7eb' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          {[
            ['Property',    job.property_address],
            ['Job',         job.title],
            ['Category',    job.category],
            ['Priority',    <span style={{ color: PRIORITY_COLOURS[job.priority] || '#1e40af', fontWeight: 700 }}>{PRIORITY_LABELS[job.priority] || job.priority}</span>],
            ['Date Raised', job.reported_date ? (() => { const [y,m,d] = job.reported_date.substring(0,10).split('-'); return `${d}/${m}/${y}`; })() : '—'],
          ].map(([label, value]) => (
            <tr key={label}>
              <td style={{ padding: '0.4rem 0.75rem 0.4rem 0', color: '#6b7280', width: '35%', verticalAlign: 'top' }}>{label}</td>
              <td style={{ padding: '0.4rem 0', fontWeight: 500 }}>{value}</td>
            </tr>
          ))}
        </table>
        {job.description && (
          <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.35rem' }}>Description</div>
            <div style={{ fontSize: '0.875rem' }}>{job.description}</div>
          </div>
        )}
      </div>

      {/* Quote form */}
      <form onSubmit={handleSubmit}>
        {errorMsg && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#dc2626', fontSize: '0.875rem' }}>
            {errorMsg}
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem' }}>
            Quote Amount ({symbolFor(job && job.currency)}) *
          </label>
          <input
            ref={amountRef}
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            required
            inputMode="decimal"
            style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '1rem', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem' }}>
            Notes <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea
            ref={notesRef}
            rows={4}
            placeholder="Any details about the work, materials, timeline or conditions..."
            style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{ width: '100%', padding: '0.875rem', background: submitting ? '#6b7280' : '#1a3c34', color: '#fff', border: 'none', borderRadius: '0.5rem', fontSize: '1rem', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer' }}
        >
          {submitting ? 'Submitting...' : '📋 Submit Quote'}
        </button>

        <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#9ca3af', marginTop: '1rem', marginBottom: 0 }}>
          This link is single-use and expires after 7 days.
        </p>
      </form>

      <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
      <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.75rem', textAlign: 'center' }}>
        casavello by Hibernian Technologies Limited
      </p>
    </Card>
  );
}
